import { useEffect, useState, startTransition } from "react";
import {
  DESKS,
  HUMAN_STAGE_TRANSITIONS,
  LOCAL_WORKUP_KEY,
  api,
  type DeskId,
  type PipelineStage,
  type QueueItem,
} from "./api";

function stageOptions(current: PipelineStage): PipelineStage[] {
  const next = HUMAN_STAGE_TRANSITIONS[current] ?? [];
  return [current, ...next.filter((s) => s !== current)];
}

function stageBadgeClass(stage: PipelineStage): string {
  switch (stage) {
    case "new":
      return "bg-[color:var(--color-sea)]/12 text-[color:var(--color-sea)]";
    case "review":
    case "pursue":
      return "bg-[color:var(--color-brass)]/18 text-[color:var(--color-brass-deep)]";
    case "drafted":
    case "approved":
      return "bg-[color:var(--color-ink)]/8 text-[color:var(--color-ink)]";
    case "mailed":
      return "bg-[color:var(--color-ok)]/15 text-[color:var(--color-ok)]";
    case "retained":
      return "bg-[color:var(--color-ok)]/25 text-[color:var(--color-ok)]";
    case "closed":
      return "bg-black/8 text-[color:var(--color-ink-soft)]";
    default:
      return "bg-black/5";
  }
}

export function DeskQueue({ onLogout }: { onLogout: () => void }) {
  const [desk, setDesk] = useState<DeskId>("patent");
  const [showClosed, setShowClosed] = useState(false);
  const [rawView, setRawView] = useState(false);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [packetDate, setPacketDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [hasLocalWorkups, setHasLocalWorkups] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_WORKUP_KEY);
      setHasLocalWorkups(Boolean(raw && raw !== "{}"));
    } catch {
      setHasLocalWorkups(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .queue(desk, showClosed)
      .then((data) => {
        if (!cancelled) {
          startTransition(() => setItems(data.results));
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load queue");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [desk, showClosed]);

  async function changeStage(item: QueueItem, stage: PipelineStage) {
    try {
      const row = await api.setStage(desk, String(item.id), stage);
      setItems((prev) =>
        prev.map((it) =>
          String(it.id) === String(item.id)
            ? { ...it, workup: { ...(it.workup ?? row), ...row } }
            : it,
        ),
      );
      if (stage === "closed" && !showClosed) {
        setItems((prev) => prev.filter((it) => String(it.id) !== String(item.id)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stage update failed");
    }
  }

  async function importLocal() {
    setImportMsg(null);
    try {
      const raw = localStorage.getItem(LOCAL_WORKUP_KEY);
      if (!raw) {
        setImportMsg("No localStorage nf_workup found.");
        return;
      }
      const parsed = JSON.parse(raw) as Record<string, Record<string, unknown>>;
      const result = await api.importWorkups(desk, parsed);
      localStorage.removeItem(LOCAL_WORKUP_KEY);
      setHasLocalWorkups(false);
      setImportMsg(`Imported ${result.imported}, skipped ${result.skipped}. Local workups cleared.`);
      const data = await api.queue(desk, showClosed);
      setItems(data.results);
    } catch (err) {
      setImportMsg(err instanceof Error ? err.message : "Import failed");
    }
  }

  return (
    <div className="mx-auto min-h-dvh max-w-6xl px-4 py-6 sm:px-6">
      <header className="animate-fade-up flex flex-col gap-4 border-b border-[color:var(--color-brass)]/30 pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium tracking-[0.16em] text-[color:var(--color-sea)] uppercase">
            Case desks · staging
          </p>
          <h1 className="mt-1 font-[family-name:var(--font-display)] text-3xl text-[color:var(--color-ink)] sm:text-4xl">
            Newark Law Offices
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-soft)]">
            Ranked CourtListener queue with D1 pipeline stages. Workups no longer live in the
            browser.
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            await api.logout();
            onLogout();
          }}
          className="self-start rounded-md border border-[color:var(--color-ink)]/15 bg-white/50 px-3 py-1.5 text-sm hover:bg-white"
        >
          Sign out
        </button>
      </header>

      <div className="animate-fade-up-delay mt-5 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        <label className="text-sm">
          <span className="mr-2 text-[color:var(--color-ink-soft)]">Desk</span>
          <select
            value={desk}
            onChange={(e) => setDesk(e.target.value as DeskId)}
            className="rounded-md border border-[color:var(--color-ink)]/15 bg-white/70 px-2 py-1.5"
          >
            {DESKS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          Show closed
        </label>

        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={rawView} onChange={(e) => setRawView(e.target.checked)} />
          Raw view
        </label>

        <a
          href={api.exportCsvUrl(desk)}
          className="rounded-md border border-[color:var(--color-ink)]/15 bg-white/60 px-3 py-1.5 text-sm hover:bg-white"
        >
          Export workups CSV
        </a>

        {hasLocalWorkups ? (
          <button
            type="button"
            onClick={importLocal}
            className="rounded-md bg-[color:var(--color-brass)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[color:var(--color-brass-deep)]"
          >
            Import local workups
          </button>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="inline-flex items-center gap-2">
            Packet date
            <input
              type="date"
              value={packetDate}
              onChange={(e) => setPacketDate(e.target.value)}
              className="rounded-md border border-[color:var(--color-ink)]/15 bg-white/70 px-2 py-1"
            />
          </label>
          <a
            href={api.packetPdfUrl(packetDate)}
            className="rounded-md bg-[color:var(--color-sea)] px-3 py-1.5 font-medium text-white hover:bg-[color:var(--color-ink)]"
          >
            Letter packet PDF
          </a>
          <a
            href={api.packetCsvUrl(packetDate)}
            className="rounded-md border border-[color:var(--color-sea)]/30 px-3 py-1.5 text-[color:var(--color-sea)] hover:bg-white/70"
          >
            Mailing log CSV
          </a>
        </div>
      </div>

      {importMsg ? <p className="mt-3 text-sm text-[color:var(--color-ink-soft)]">{importMsg}</p> : null}
      {error ? (
        <p className="mt-3 text-sm text-[color:var(--color-danger)]" role="alert">
          {error}
        </p>
      ) : null}

      <section className="mt-6">
        {loading ? (
          <p className="animate-pulse-soft text-sm text-[color:var(--color-ink-soft)]">
            Loading ranked queue…
          </p>
        ) : rawView ? (
          <pre className="overflow-x-auto rounded-md bg-[color:var(--color-ink)] p-4 text-xs text-[color:var(--color-paper)]">
            {JSON.stringify(items, null, 2)}
          </pre>
        ) : items.length === 0 ? (
          <p className="text-sm text-[color:var(--color-ink-soft)]">
            No filings in this desk queue{showClosed ? "" : " (closed hidden)"}.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((item, idx) => {
              const stage = item.workup?.stage ?? "new";
              return (
                <li
                  key={String(item.id)}
                  className="animate-fade-up border-b border-[color:var(--color-ink)]/8 py-4"
                  style={{ animationDelay: `${Math.min(idx, 8) * 0.03}s` }}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-[family-name:var(--font-display)] text-lg text-[color:var(--color-ink)]">
                          {item.caseName || "Untitled matter"}
                        </span>
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${stageBadgeClass(stage)}`}
                        >
                          {stage}
                        </span>
                        <span className="text-xs text-[color:var(--color-ink-soft)]">
                          score {item.score}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-[color:var(--color-ink-soft)]">
                        {[item.docketNumber, item.court || item.courtId, item.state, item.dateFiled]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      {item.defendants?.length ? (
                        <p className="mt-1 text-sm">
                          <span className="text-[color:var(--color-ink-soft)]">Defendants: </span>
                          {item.defendants.slice(0, 4).join("; ")}
                          {item.defendants.length > 4 ? "…" : ""}
                        </p>
                      ) : null}
                      {item.signals?.length ? (
                        <p className="mt-1 text-xs tracking-wide text-[color:var(--color-brass-deep)] uppercase">
                          {item.signals.join(" · ")}
                        </p>
                      ) : null}
                      {item.docketUrl ? (
                        <a
                          href={item.docketUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-block text-sm text-[color:var(--color-sea)] underline-offset-2 hover:underline"
                        >
                          Open docket
                        </a>
                      ) : null}
                    </div>
                    <label className="shrink-0 text-sm">
                      <span className="sr-only">Stage</span>
                      <select
                        value={stage}
                        onChange={(e) => changeStage(item, e.target.value as PipelineStage)}
                        className="w-full rounded-md border border-[color:var(--color-ink)]/15 bg-white/80 px-2 py-1.5 sm:w-40"
                      >
                        {stageOptions(stage).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
